import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { getTenantSubdomain, updateCurrentUserProfile } from '../services/api';

const THEME_STORAGE_PREFIX = 'followus_theme';
const THEME_PREFERENCE_LS_KEY = (tenant) =>
  tenant ? `${THEME_STORAGE_PREFIX}_tenant_${tenant}` : THEME_STORAGE_PREFIX;

const PREFERENCE_LIGHT = 'light';
const PREFERENCE_DARK = 'dark';
const PREFERENCE_SYSTEM = 'system';

function getStoredPreference() {
  try {
    if (typeof window === 'undefined') return PREFERENCE_LIGHT;
    const tenant = getTenantSubdomain();
    const key = THEME_PREFERENCE_LS_KEY(tenant);
    const stored = localStorage.getItem(key);
    if (stored === PREFERENCE_LIGHT || stored === PREFERENCE_DARK || stored === PREFERENCE_SYSTEM) {
      return stored;
    }
  } catch (_) {}
  return PREFERENCE_SYSTEM;
}

function getSystemPrefersDark() {
  if (typeof window === 'undefined') return false;
  return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function resolveEffectiveTheme(preference, systemDark) {
  if (preference === PREFERENCE_DARK) return 'dark';
  if (preference === PREFERENCE_LIGHT) return 'light';
  return systemDark ? 'dark' : 'light';
}

function applyThemeToDocument(effectiveTheme) {
  const root = document.documentElement;
  if (effectiveTheme === 'dark') {
    root.classList.add('dark');
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', '#0f172a');
  } else {
    root.classList.remove('dark');
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', '#000000');
  }
}

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const [preference, setPreferenceState] = useState(getStoredPreference);
  const [systemDark, setSystemDark] = useState(getSystemPrefersDark);

  const resolvedTheme = useMemo(
    () => resolveEffectiveTheme(preference, systemDark),
    [preference, systemDark]
  );

  useEffect(() => {
    applyThemeToDocument(resolvedTheme);
  }, [resolvedTheme]);

  useEffect(() => {
    if (preference !== PREFERENCE_SYSTEM) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => setSystemDark(!!e.matches);
    mq.addEventListener('change', handleChange);
    return () => mq.removeEventListener('change', handleChange);
  }, [preference]);

  const setThemePreference = (value) => {
    if (value !== PREFERENCE_LIGHT && value !== PREFERENCE_DARK && value !== PREFERENCE_SYSTEM) return;
    setPreferenceState(value);
    try {
      const tenant = getTenantSubdomain();
      const key = THEME_PREFERENCE_LS_KEY(tenant);
      localStorage.setItem(key, value);
    } catch (_) {}
    try {
      updateCurrentUserProfile({ theme_preference: value }).catch(() => {});
    } catch (_) {}
  };

  const value = useMemo(
    () => ({
      themePreference: preference,
      resolvedTheme,
      setThemePreference,
      PREFERENCE_LIGHT,
      PREFERENCE_DARK,
      PREFERENCE_SYSTEM,
    }),
    [preference, resolvedTheme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}

export { PREFERENCE_LIGHT, PREFERENCE_DARK, PREFERENCE_SYSTEM };
